import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import BrandLogo from '../components/shared/BrandLogo';
import ThemeToggle from '../components/shared/ThemeToggle';
import { useAuth } from '../hooks/useAuth';
import gdnLogo from '../assets/gdn-logo.jpeg';

const gdnProfiles = ['super_admin', 'gdn_admin'];

export default function Login() {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState(() => localStorage.getItem('mecsync-remember-email') || '');
  const [senha, setSenha] = useState('');
  const [lembrar, setLembrar] = useState(() => Boolean(localStorage.getItem('mecsync-remember-email')));
  const [error, setError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const { user, perfil, loading, signIn } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && user && perfil) {
      nav(gdnProfiles.includes(perfil.perfil) ? '/gdn/clientes' : '/dashboard', { replace: true });
    }
  }, [loading, nav, perfil, user]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setAuthLoading(true);
    
    if (!email || !senha) {
      setError('Preencha email e senha');
      setAuthLoading(false);
      return;
    }
    
    try {
      const signedIn = await signIn(email, senha);
      if (lembrar) {
        localStorage.setItem('mecsync-remember-email', email.trim().toLowerCase());
      } else {
        localStorage.removeItem('mecsync-remember-email');
      }
      nav(gdnProfiles.includes(signedIn.profile?.perfil) ? '/gdn/clientes' : '/dashboard');
    } catch (err) {
      if (err.message?.toLowerCase().includes('invalid login credentials')) {
        setError('Email ou senha inválidos. Verifique os dados e tente novamente.');
      } else if (err.message?.toLowerCase().includes('conta excluida')) {
        setError('Esta conta foi excluida ou esta inativa. Fale com a GDN para reativar o acesso.');
      } else {
        setError(err.message || 'Não foi possível fazer login. Tente novamente.');
      }
    } finally {
      setAuthLoading(false);
    }
  };

  
  return (
    <div className="relative grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <ThemeToggle className="absolute right-4 top-4 z-10" />
      <div className="login-gdn-panel hidden lg:flex flex-col justify-between bg-gradient-to-br from-[#0f172a] via-[#101b31] to-[#0f172a] p-12 text-white">
        <div className="mx-auto mt-10 max-w-md text-center">
          <img src={gdnLogo} alt="GDN Corp" className="mx-auto w-[180px] rounded-lg bg-[#0f172a] object-contain p-2" />
          <p className="login-gdn-tagline mt-5 text-lg text-sky-300">Soluções em Automação e Tecnologia</p>
          <div className="login-gdn-copy mt-8 space-y-3 text-left text-sm text-zinc-200">
            <p>✦ Automação inteligente para seu negócio</p>
            <p>✦ Sistemas sob medida</p>
            <p>✦ Suporte dedicado 24/7</p>
            <p>✦ Tecnologia de ponta acessível</p>
          </div>
        </div>
        <p className="login-gdn-footer text-center text-xs text-zinc-500"> 2026 GDN Corp — Todos os direitos reservados</p>
      </div>
      
      <div className="flex items-center justify-center bg-bg p-4">
        <Card className="w-full max-w-md">
          <div className="mb-4">
            <BrandLogo size="md" />
            <p className="text-sm text-zinc-400">Acesse sua oficina digital</p>
          </div>
          
          <h2 className="font-display text-2xl">Entrar na conta</h2>
          <p className="mb-4 text-sm text-zinc-400">Use seu email e senha cadastrados.</p>
          
          <form className="space-y-3" onSubmit={submit}>
            <div className="relative">
              <Mail className="absolute left-3 top-2.5 text-zinc-500" size={16} />
              <Input 
                className="pl-9" 
                type="email"
                placeholder="Email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                autoComplete="username"
                disabled={loading}
              />
            </div>
            
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 text-zinc-500" size={16} />
              <Input 
                className="pl-9 pr-10" 
                type={show ? 'text' : 'password'} 
                placeholder="Senha" 
                value={senha} 
                onChange={(e) => setSenha(e.target.value)} 
                autoComplete="current-password"
                disabled={loading}
              />
              <button 
                type="button" 
                className="absolute right-2 top-2 text-zinc-400" 
                onClick={() => setShow(!show)}
                disabled={loading}
              >
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={lembrar} 
                  onChange={(e) => setLembrar(e.target.checked)} 
                  disabled={loading}
                />
                Lembrar-me
              </label>
            </div>
            
            {error && (
              <div className="rounded-md bg-red-500/10 p-3 text-sm text-red-400">
                {error}
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full justify-center" 
              disabled={authLoading}
            >
              {authLoading ? <LoadingSpinner size="sm" /> : 'Entrar'}
            </Button>

            <p className="text-center text-sm text-zinc-400">
              Ainda não tem acesso? <Link className="font-semibold text-orange-400 hover:text-orange-300" to="/teste-gratis">Teste grátis por 7 dias</Link>
            </p>
          </form>
          
                  </Card>
      </div>
    </div>
  );
}

